import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import TopBar from '../components/TopBar';
import PlusCard from '../components/PlusCard';
import { canActivateIncluded, plusStartedNotice } from '../utils/plusStatus';
import '../styles/Alerts.css';

// ── /plus/:deviceId — "Your first year of Plus is included" ──────────────
//
// The last step after claiming (and setting up) a monitor: activate the
// included year with a card ($0 today), or "Not now". Only for the owner of a
// monitor that hasn't used its included year and isn't comped; anyone else
// goes straight on to the dashboard, so callers can send everyone here.
//
// Router state is passed through to the dashboard (the setup wizard's
// "Saved…" toast rides on it).

export default function ActivatePlus({ user }) {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState(null); // { plus, isOwner }

  const goOn = () => navigate('/dashboard', { replace: true, state: location.state || undefined });

  useEffect(() => {
    if (!user || !deviceId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const [devSnap, claimSnap] = await Promise.all([
          getDoc(doc(db, 'devices', deviceId)),
          getDoc(doc(db, 'users', user.uid, 'devices', deviceId)),
        ]);
        if (cancelled) return;
        const dev = devSnap.exists() ? devSnap.data() : null;
        const isOwner = claimSnap.exists() && claimSnap.data().role === 'owner';
        if (!dev || !isOwner) { goOn(); return; }
        // Bought on the website: its included year started by itself at
        // claim. Say so once, with the renewal terms, instead of skipping.
        const notice = plusStartedNotice(dev.plus);
        if (notice) { setState({ notice }); return; }
        if (!canActivateIncluded(dev.plus)) { goOn(); return; }
        setState({ plus: dev.plus || null, isOwner });
      } catch (err) {
        console.error('ActivatePlus: load', err);
        if (!cancelled) goOn();
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, deviceId]);

  return (
    <div className="app-container">
      <TopBar subtitle="SoKo Plus" />
      <div className="page-content">
        {!state ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}><div className="spinner" /></div>
        ) : state.notice ? (
          <section className="plus-card plus-card--trial" aria-label="SoKo Plus">
            <div className="plus-card__head">
              <div className="plus-card__main">
                <div className="plus-card__title">{state.notice.title}</div>
                {state.notice.lines.map((l) => <div key={l} className="plus-card__line">{l}</div>)}
              </div>
            </div>
            <button type="button" className="btn btn-primary plus-card__btn" onClick={goOn}>Continue</button>
          </section>
        ) : (
          <PlusCard deviceId={deviceId} plus={state.plus} isOwner={state.isOwner} onNotNow={goOn} />
        )}
      </div>
    </div>
  );
}
